import { useState } from 'react';
import {
  controlledCountConfirm,
  controlledCountSave,
  controlledCountSheet,
  controlledCounts,
  controlledReceipt,
  type ApiClient,
  type ApiError,
  type ControlledCount,
  type CountSheetLine,
} from '@aithora/shared';
import { ErrorNote } from '../../../ErrorNote';
import { useQuery } from '../../../useQuery';
import { A, sx } from '../parts';
import { PocDialog } from './PocDialog';
import { ALERT, CARD, CODE, NOTE, NUM, chip, hhmm, prim } from './styles';

const num = (v: string | undefined) => {
  const t = String(v ?? '').trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
};
const u = (unit: string | null) => (unit ? ` ${unit}` : '');
const medName = (l: { name: string; dosage: string | null }) => (l.dosage && !l.name.includes(l.dosage) ? `${l.name} ${l.dosage}` : l.name);
/** "Matches" / "Off by 2" against a number, as the design writes it as you type. */
const versus = (v: number | null, against: number | null, unit: string | null): { text: string; ok: boolean } | null => {
  if (v === null) return null;
  if (against === null) return { text: 'First count', ok: true };
  const d = Math.round(Math.abs(v - against) * 1000) / 1000;
  return d === 0 ? { text: 'Matches', ok: true } : { text: `Off by ${d}${u(unit)}`, ok: false };
};

const BAD_BEFORE_SAVE = 'The count does not match. Saving it alerts the charge nurse and the DON at once. Neither nurse leaves until it is found or reported.';

/**
 * The controlled-drug count at shift change (design P.vm count): two nurses, each on their
 * own sign-in. The first counts every controlled medicine and saves (POST /controlled-counts);
 * a different nurse counts again and confirms (POST /controlled-counts/:id/confirm). A
 * mismatch is saved as a discrepancy and alerts at once; it is never shown as matching.
 */
export function CountSheet({ client, meName, onClose, onToast }: { client: ApiClient; meName: string; onClose: () => void; onToast: (t: string) => void }) {
  const sheet = useQuery((signal) => controlledCountSheet(client, { signal }), [client]);
  const recent = useQuery((signal) => controlledCounts(client, { query: { limit: 5 }, signal }), [client]);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [fresh, setFresh] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<(ControlledCount & { message: string; mismatch: boolean }) | null>(null);
  const [delivery, setDelivery] = useState<{ open: boolean; med: string; qty: string; busy: boolean; error: ApiError | null }>({ open: false, med: '', qty: '', busy: false, error: null });

  // A count still waiting for its second nurse (the newest one only).
  const latest = recent.data?.counts[0] ?? null;
  const pending = !fresh && latest && !latest.confirmedBy ? latest : null;
  const shown = result ?? pending;
  const lines: CountSheetLine[] = sheet.data?.lines ?? [];
  const second = !!pending && !result;

  const rows = second
    ? pending!.lines.map((l) => ({ id: l.medicationId, name: medName(l), who: l.resident ?? 'Resident', unit: l.unit, against: l.counted, sub: `${l.resident ?? 'Resident'} · first nurse counted ${l.counted}${u(l.unit)}` }))
    : lines.map((l) => ({ id: l.medicationId, name: medName(l), who: l.resident ?? 'Resident', unit: l.unit, against: l.expected, sub: l.expected === null ? `${l.resident ?? 'Resident'} · first count, no number on record yet` : `${l.resident ?? 'Resident'} · should be ${l.expected}${u(l.unit)}` }));
  const all = rows.length > 0 && rows.every((r) => num(vals[r.id]) !== null);
  const bad = rows.some((r) => versus(num(vals[r.id]), r.against, r.unit)?.ok === false);
  const canSave = all && !busy && !result;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    const body = rows.map((r) => ({ medicationId: r.id, counted: num(vals[r.id])! }));
    try {
      const r = second ? await controlledCountConfirm(client, { params: { id: pending!.id }, body: { recount: body } }) : await controlledCountSave(client, { body: { lines: body } });
      setVals({});
      if (second && !r.mismatch && r.status !== 'discrepancy') {
        onToast(r.message);
        onClose();
        return;
      }
      setResult(r);
      sheet.reload();
      recent.reload();
    } catch (e) {
      setError(e as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const addDelivery = async () => {
    const q = num(delivery.qty);
    if (!delivery.med || q === null || q <= 0 || delivery.busy) return;
    setDelivery((d) => ({ ...d, busy: true, error: null }));
    try {
      await controlledReceipt(client, { body: { medicationId: delivery.med, quantity: q } });
      setDelivery({ open: false, med: '', qty: '', busy: false, error: null });
      onToast('Delivery recorded. The next count expects it.');
      sheet.reload();
    } catch (e) {
      setDelivery((d) => ({ ...d, busy: false, error: e as ApiError }));
    }
  };

  const missing = (error?.body as { missing?: { name: string; resident: string | null }[] } | null)?.missing;

  return (
    <PocDialog
      title="Controlled drug count"
      onClose={onClose}
      testId="poc-count"
      action={
        !result && rows.length > 0 ? (
          <A style={prim(canSave)} onClick={() => void save()} disabled={!canSave}>
            {second ? 'Confirm count' : 'Save count'}
          </A>
        ) : undefined
      }
    >
      {(sheet.error || recent.error) && <ErrorNote error={(sheet.error ?? recent.error)!} onRetry={() => { sheet.reload(); recent.reload(); }} />}
      {(sheet.loading || recent.loading) && !(sheet.data && recent.data) && <span style={sx('font-size:16px;color:#54606C;')}>Loading the count sheet</span>}
      {shown && (
        <div style={sx(CARD)} data-testid="count-summary">
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>
            {`Counted by ${shown.countedBy.name ?? 'a nurse'}${shown.countedBy.at ? ` · ${hhmm(shown.countedBy.at)}` : ''}`}
            {shown.confirmedBy ? ` · Confirmed by ${shown.confirmedBy.name ?? 'a second nurse'}${shown.confirmedBy.at ? ` · ${hhmm(shown.confirmedBy.at)}` : ''}` : ''}
          </span>
          {shown.lines.map((l) => (
            <span key={l.medicationId} style={sx(`font-size:16px;font-weight:700;color:${l.result === 'off' ? '#B3261E' : l.result === 'matches' ? '#1F6B52' : '#3C454F'};`)}>
              {`${medName(l)} · ${l.resident ?? 'Resident'}: ${l.text}${l.secondCounted !== null ? ` · second nurse counted ${l.secondCounted}${u(l.unit)}` : ''}`}
            </span>
          ))}
          {!shown.confirmedBy && shown.status !== 'discrepancy' && <span style={sx('font-size:16px;color:#3C454F;')}>A second nurse counts again and confirms on their own sign-in.</span>}
        </div>
      )}
      {shown && (shown.status === 'discrepancy' || (result?.mismatch ?? false)) && (
        <div role="alert" style={sx(ALERT)}>
          {result?.message ?? 'The count does not match. The charge nurse and the DON are alerted now. Neither nurse leaves until it is found or reported.'}
        </div>
      )}
      {result && !result.mismatch && result.status !== 'discrepancy' && <span role="status" style={sx('font-size:16px;font-weight:700;color:#1F6B52;')}>{result.message}</span>}
      {!result && rows.map((r) => {
        const v = versus(num(vals[r.id]), r.against, r.unit);
        return (
          <div key={r.id} style={sx(CARD)}>
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>{r.name}</span>
            <span style={sx('font-size:16px;color:#54606C;')}>{r.sub}</span>
            <div style={sx('display:flex;align-items:center;gap:10px;')}>
              <input
                type="text"
                inputMode="decimal"
                value={vals[r.id] ?? ''}
                onChange={(e) => setVals((p) => ({ ...p, [r.id]: e.target.value }))}
                placeholder="Count"
                aria-label={`Count of ${r.name} for ${r.who}`}
                style={sx(`flex:0 0 100px;width:100px;${NUM}`)}
              />
              {v && <span style={sx(`font-size:16px;font-weight:800;color:${v.ok ? '#1F6B52' : '#B3261E'};`)}>{v.text}</span>}
            </div>
          </div>
        );
      })}
      {!result && sheet.data && lines.length === 0 && !second && <span style={sx('font-size:16px;color:#3C454F;')}>No controlled medicines are on order in this building.</span>}
      {!result && bad && (
        <div role="alert" style={sx(ALERT)}>
          {BAD_BEFORE_SAVE}
        </div>
      )}
      {!result && rows.length > 0 && (
        <span style={sx(chip(false) + 'align-self:flex-start;')}>{second ? `Second nurse: ${meName} · signs when you confirm` : `First nurse: ${meName} · signs when you save`}</span>
      )}
      {error && <ErrorNote error={error} />}
      {missing && missing.length > 0 && <span style={sx('font-size:16px;color:#8A1C16;font-weight:700;')}>{`Not counted yet: ${missing.map((m) => `${m.name} (${m.resident ?? 'resident'})`).join(', ')}`}</span>}
      {pending && !result && (
        <A style="align-self:flex-start;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;" onClick={() => { setFresh(true); setVals({}); setError(null); }}>
          Start a new count instead
        </A>
      )}
      {result && (
        <A style="align-self:flex-start;font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;" onClick={() => { setResult(null); setFresh(false); }}>
          Back to the count
        </A>
      )}
      {lines.length > 0 && !delivery.open && (
        <A style="align-self:flex-start;min-height:36px;padding:0 12px;border-radius:9px;border:1px dashed rgba(34,39,46,.35);background:#FFFFFF;font-size:16px;font-weight:700;color:#264C7E;text-decoration:none;display:inline-flex;align-items:center;" onClick={() => setDelivery((d) => ({ ...d, open: true }))}>
          Record a pharmacy delivery
        </A>
      )}
      {delivery.open && (
        <div style={sx(CARD)} data-testid="count-delivery">
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>Pharmacy delivery</span>
          <select aria-label="Medicine delivered" value={delivery.med} onChange={(e) => setDelivery((d) => ({ ...d, med: e.target.value }))} style={sx(CODE)}>
            <option value="">Pick the medicine</option>
            {lines.map((l) => (
              <option key={l.medicationId} value={l.medicationId}>{`${medName(l)} · ${l.resident ?? 'Resident'}`}</option>
            ))}
          </select>
          <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;')}>
            <input type="text" inputMode="decimal" aria-label="How many arrived" placeholder="How many" value={delivery.qty} onChange={(e) => setDelivery((d) => ({ ...d, qty: e.target.value }))} style={sx(`flex:0 0 100px;width:100px;${NUM}`)} />
            <A style={prim(!!delivery.med && (num(delivery.qty) ?? 0) > 0 && !delivery.busy)} onClick={() => void addDelivery()} disabled={!delivery.med || !((num(delivery.qty) ?? 0) > 0) || delivery.busy}>
              Save delivery
            </A>
            <A style="font-size:16px;font-weight:700;color:#54606C;text-decoration:none;" onClick={() => setDelivery({ open: false, med: '', qty: '', busy: false, error: null })}>
              Cancel
            </A>
          </div>
          {delivery.error && <ErrorNote error={delivery.error} compact />}
        </div>
      )}
      <span style={sx(NOTE)}>{sheet.data?.note ?? 'Two nurses count together at every shift change. A mismatch can never be saved as matching.'}</span>
    </PocDialog>
  );
}
